import uniq from "lodash/uniq";
import { useAgents } from "../../admin/agents/useAgents";
import { useProducts } from "../../admin/products/useProducts";
import { formatDate } from "../../data/dataUtils";
import type { EmailTemplate, EmailTemplateEntry } from "../../misc/emailTemplates";
import type { SavedOrder } from "../orderTypes";
import { useCustomerContact } from "./useCustomerContact";

interface EmailTemplateOrderProduct {
  readonly id: string;
  readonly name: string;
  readonly date: string;
  readonly bookingNumber?: string;
  readonly providerName: string;
  readonly options: { readonly id: string; readonly quantity: number; readonly name: string }[];
}

interface EmailTemplateOrder {
  readonly id: string;
  readonly userFriendlyId: number;
  readonly customerId: string;
  readonly name: string;
  readonly products: EmailTemplateOrderProduct[];
  readonly files: { readonly name: string; readonly url: string }[];
}

export function useCustomerEmailTemplates(savedOrder: SavedOrder): {
  isLoading: boolean;
  templates: EmailTemplateEntry[];
} {
  const { contact, accountManagerId, isLoading: isCustomerLoading } = useCustomerContact(savedOrder.customerId);
  const { products, isLoading: areProductsLoading } = useProducts();
  const { agents, isLoading: areAgentsLoading } = useAgents();
  const isLoading = isCustomerLoading || areProductsLoading || areAgentsLoading;
  if (isLoading || !contact) {
    return { isLoading, templates: [] };
  }
  const supervisorId = savedOrder.supervisorId ?? accountManagerId;
  const supervisor = agents.find((a) => a.id === supervisorId);
  const supervisorEmail = supervisor?.contact?.przezmorzeEmail;
  const ccEmails = supervisorEmail ? [supervisorEmail] : [];

  const order = {
    ...savedOrder,
    products: savedOrder.products
      .filter((p) => !p.cancelled)
      .map((p) => {
        const matchingProduct = products.find((pr) => pr.id === p.id);
        return {
          ...p,
          name: matchingProduct?.name ?? "",
          providerName: matchingProduct?.provider.name ?? "",
          options: p.options.map((o) => ({
            ...o,
            name: matchingProduct?.options.find((op) => op.id === o.id)?.name ?? "",
          })),
        };
      }),
  };
  return {
    isLoading,
    templates: [
      {
        name: "Potwierdzenie zamówienia",
        get template() {
          return new OrderConfirmationTemplate([contact.email], order);
        },
      },
      {
        name: "Brak potwierdzenia",
        get template() {
          return new NoConfirmationTemplate([contact.email], order, ccEmails);
        },
      },
      {
        name: "Potwierdzenie rezerwacji z listą: " + contact.name,
        get template() {
          return new ListConfirmationTemplate([contact.email], order);
        },
      },
      {
        name: "Karta zamówienia",
        get template() {
          return new OrderCardTemplate([contact.email], order, contact.loginEmails);
        },
      },
    ],
  };
}

class OrderConfirmationTemplate implements EmailTemplate {
  constructor(
    readonly emails: string[],
    private readonly order: EmailTemplateOrder,
  ) {}

  get subject() {
    const uniqBookingNumbers = uniq(this.order.products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return `Potwierdzenie zam. nr ${uniqBookingNumbers.join(", ")}`;
  }

  get body() {
    const uniqBookingNumbers = uniq(this.order.products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return `Szanowni Państwo,
niniejszym potwierdzamy rezerwację pakietu promowego zgodnie z zestawieniem poniżej:

nazwa grupy: ${this.order.name}
nr rezerwacji:
${this.order.products.map((s) => [s.providerName, s.bookingNumber].join(" ")).join("\n")}

Potwierdzenie rezerwacji znajdą Państwo pod linkiem:
${this.order.files
  .filter((f) => uniqBookingNumbers.some((b) => f.name.includes(b)))
  .map((f) => f.url)
  .join("\n")}

Data świadczenia /trasa/zamówione kabiny/autokar/posiłki
${this.order.products.map((p) => [p.date, p.name, optionText(p)].join(" ")).join("\n")}`;
  }
}

class ListConfirmationTemplate implements EmailTemplate {
  constructor(
    readonly emails: string[],
    private readonly order: EmailTemplateOrder,
  ) {}

  get subject() {
    const uniqBookingNumbers = uniq(this.order.products.map((p) => p.bookingNumber).filter((b): b is string => !!b));
    return `Potwierdzenie rezerwacji ${uniqBookingNumbers.join(", ")} LISTAs`;
  }

  get body() {
    return `Dotyczy rezerwacji: ${this.order.name}

${this.order.products.map((p) => [p.providerName, p.bookingNumber, p.name, formatDate(p.date)].join(" ")).join("\n")}

Listę do wypełnienia znajdą Państwo pod adresem:
${this.order.files
  .filter((f) => f.name.includes("LISTA"))
  .map((f) => f.url)
  .join("\n")}

Lista jest także dostępna z poziomu aplikacji.

Prosimy o wypełnienie listy najpóźniej na 10 dni przed rejsem oraz przesłanie do nas informacji, że lista w systemie jest ostateczna.

Po otrzymaniu listy prześlemy do Państwa ostateczne potwierdzenie rezerwacji.`;
  }
}

function optionText(product: EmailTemplateOrderProduct): string {
  return product.options.map((option) => `${option.name ?? ""}: ${option.quantity}`).join(", ");
}

class OrderCardTemplate implements EmailTemplate {
  constructor(
    readonly emails: string[],
    private readonly order: EmailTemplateOrder,
    private readonly loginEmails: string[],
  ) {}

  get subject() {
    return `Odprawa wyjazdu ${this.order.userFriendlyId}`;
  }

  get body() {
    return `Wszystkie szczegóły dotyczące zamówienia (potwierdzenia, vouchery itp.) ${this.order.userFriendlyId} ${this.order.name} znajdziesz pod linkiem:

${window.location.host}/orders/${this.order.id}

Mail do logowania: ${this.loginEmails.join(", ")}

Po zalogowaniu wszystkie widoczne dokumenty możecie pobrać lub samodzielnie wysyłać na Wasz adres mailowy.`;
  }
}

class NoConfirmationTemplate implements EmailTemplate {
  constructor(
    readonly emails: string[],
    private readonly order: EmailTemplateOrder,
    readonly ccEmails: string[],
  ) {}

  get subject() {
    return `Status potwierdzenia zamówienia ${this.order.userFriendlyId}`;
  }

  get body() {
    return `Dzień dobry,
    
uprzejmie informujemy, że czas potwierdzenia rezerwacji zamówienia ${this.order.userFriendlyId} uległ wydłużeniu z uwagi na [WPISUJEMY POWÓD I OBECNĄ SYTUACJĘ (brak odpowiedzi ze strony dostawców lub konieczność poszukiwania dostępnych alternatyw)].

Pracujemy nad finalizacją rezerwacji i wrócimy z potwierdzeniem tak szybko, jak będzie to możliwe.

Dziękujemy za cierpliwość i zrozumienie.

Z wyrazami szacunku,
`;
  }
}
